<template>
    <div class="financial-reports-container container">
      <el-card shadow="hover">
        <template #header>
          <div class="card-header">
            <h2>التقارير المالية</h2>
          </div>
        </template>
  
        <el-tabs v-model="activeTab">
          <!-- Treasury Movement Report Tab -->
          <el-tab-pane label="تقرير حركة الخزينة" name="treasury">
            <div class="report-section">
              <el-form :model="treasuryFilters" label-position="top">
                <el-row :gutter="20">
                  <el-col :span="8">
                    <el-form-item label="اختر الخزينة">
                      <el-select v-model="treasuryFilters.treasury_id" placeholder="اختر" style="width: 100%;">
                        <el-option v-for="treasury in treasuries" :key="treasury.id" :label="treasury.name" :value="treasury.id" />
                      </el-select>
                    </el-form-item>
                  </el-col>
                  <el-col :span="8">
                    <el-form-item label="من تاريخ">
                      <el-date-picker v-model="treasuryFilters.start_date" type="date" style="width: 100%;" />
                    </el-form-item>
                  </el-col>
                  <el-col :span="8">
                    <el-form-item label="إلى تاريخ">
                      <el-date-picker v-model="treasuryFilters.end_date" type="date" style="width: 100%;" />
                    </el-form-item>
                  </el-col>
                </el-row>
                <el-button type="primary" @click="generateTreasuryReport" :loading="loading.treasury">
                  <el-icon><Search /></el-icon> توليد التقرير
                </el-button>
              </el-form>
  
              <div v-if="treasuryReportData" class="report-display" id="treasury-report-printable">
                <h3>تقرير حركة الخزينة: {{ treasuryReportData.treasury_name }}</h3>
                <p>للفترة من {{ formatDateForDisplay(treasuryReportData.start_date) }} إلى {{ formatDateForDisplay(treasuryReportData.end_date) }}</p>
                
                <el-descriptions :column="2" border class="summary-descriptions">
                    <el-descriptions-item label="الرصيد الافتتاحي">{{ parseFloat(treasuryReportData.opening_balance).toFixed(2) }} دينار</el-descriptions-item>
                    <el-descriptions-item label="إجمالي الإيرادات">{{ totalIncome.toFixed(2) }} دينار</el-descriptions-item>
                    <el-descriptions-item label="إجمالي المصروفات">{{ totalExpenses.toFixed(2) }} دينار</el-descriptions-item>
                    <el-descriptions-item label="الرصيد الختامي"><strong>{{ closingBalance.toFixed(2) }} دينار</strong></el-descriptions-item>
                </el-descriptions>
  
                <el-table :data="treasuryReportData.transactions" border style="margin-top: 20px;">
                    <el-table-column label="التاريخ">
                        <template #default="{ row }">{{ formatDateForDisplay(row.payment_date) }}</template>
                    </el-table-column>
                    <el-table-column prop="description" label="الوصف" />
                    <el-table-column label="الإيرادات">
                        <template #default="{ row }">{{ row.type === 'income' ? row.amount : '-' }}</template>
                    </el-table-column>
                    <el-table-column label="المصروفات">
                        <template #default="{ row }">{{ row.type === 'expense' ? row.amount : '-' }}</template>
                    </el-table-column>
                </el-table>
                <el-button class="print-btn" type="success" @click="printReport('treasury-report-printable')"><el-icon><Printer /></el-icon> طباعة</el-button>
              </div>
            </div>
          </el-tab-pane>
  
          <!-- Income Statement Report Tab -->
          <el-tab-pane label="تقرير قائمة الدخل" name="income">
             <div class="report-section">
              <el-form :model="incomeFilters" label-position="top">
                <el-row :gutter="20">
                  <el-col :span="12">
                    <el-form-item label="من تاريخ">
                      <el-date-picker v-model="incomeFilters.start_date" type="date" style="width: 100%;" />
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item label="إلى تاريخ">
                      <el-date-picker v-model="incomeFilters.end_date" type="date" style="width: 100%;" />
                    </el-form-item>
                  </el-col>
                </el-row>
                <el-button type="primary" @click="generateIncomeStatement" :loading="loading.income">
                  <el-icon><Search /></el-icon> توليد التقرير
                </el-button>
              </el-form>
               <div v-if="incomeStatementData" class="report-display" id="income-statement-printable">
                    <h3>قائمة الدخل (الأرباح والخسائر)</h3>
                    <p>للفترة من {{ formatDateForDisplay(incomeStatementData.start_date) }} إلى {{ formatDateForDisplay(incomeStatementData.end_date) }}</p>
    
                    <h4>الإيرادات</h4>
                    <el-table :data="incomeStatementData.income_summary" border>
                        <el-table-column prop="name" label="حساب الإيراد" />
                        <el-table-column prop="total" label="المجموع" />
                    </el-table>
                    <p class="total-summary">إجمالي الإيرادات: <strong>{{ parseFloat(incomeStatementData.total_income).toFixed(2) }} دينار</strong></p>
    
                    <h4>المصروفات</h4>
                    <el-table :data="incomeStatementData.expense_summary" border>
                        <el-table-column prop="name" label="حساب المصروف" />
                        <el-table-column prop="total" label="المجموع" />
                    </el-table>
                    <p class="total-summary">إجمالي المصروفات: <strong>{{ parseFloat(incomeStatementData.total_expenses).toFixed(2) }} دينار</strong></p>
                    
                    <el-divider />
                    <div class="net-profit" :class="incomeStatementData.net_profit >= 0 ? 'profit' : 'loss'">
                        صافي الربح / (الخسارة): <strong>{{ parseFloat(incomeStatementData.net_profit).toFixed(2) }} دينار</strong>
                    </div>
                     <el-button class="print-btn" type="success" @click="printReport('income-statement-printable')"><el-icon><Printer /></el-icon> طباعة</el-button>
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
    
        </el-card>
      </div>
    </template>
    
    <script>
    import axios from 'axios';
    import { ElMessage } from 'element-plus';
    import { Search, Printer } from '@element-plus/icons-vue';
    
    export default {
      name: 'FinancialReports',
      components: { Search, Printer },
      data() {
        return {
          api: null,
          activeTab: 'treasury',
          loading: {
            treasury: false,
            income: false,
          },
          treasuries: [],
          treasuryFilters: {
            treasury_id: null,
            start_date: null,
            end_date: null,
          },
          incomeFilters: {
            start_date: null,
            end_date: null,
          },
          treasuryReportData: null,
          incomeStatementData: null,
        };
      },
      computed: {
        totalIncome() {
            if (!this.treasuryReportData) return 0;
            return this.treasuryReportData.transactions.filter(t => t.type === 'income').reduce((sum, t) => sum + parseFloat(t.amount), 0);
        },
        totalExpenses() {
            if (!this.treasuryReportData) return 0;
            return this.treasuryReportData.transactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + parseFloat(t.amount), 0);
        },
        closingBalance() {
            if (!this.treasuryReportData) return 0;
            return parseFloat(this.treasuryReportData.opening_balance) + this.totalIncome - this.totalExpenses;
        }
      },
      created() {
        this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
        this.api.interceptors.request.use((config) => {
          const token = localStorage.getItem('access_token');
          if (token) config.headers['Authorization'] = `Bearer ${token}`;
          return config;
        });
        this.fetchTreasuries();
      },
      methods: {
        formatDateForAPI(date) {
          if (!date) return null;
          const d = new Date(date);
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        },
        formatDateForDisplay(dateString) {
          if (!dateString) return '';
          const date = new Date(dateString);
          return date.toLocaleDateString('ar-EG');
        },
        async fetchTreasuries() {
          try {
            const response = await this.api.get('/treasuries');
            this.treasuries = response.data;
          } catch (error) {
            ElMessage.error('فشل في جلب الخزائن.');
          }
        },
        async generateTreasuryReport() {
          if (!this.treasuryFilters.treasury_id || !this.treasuryFilters.start_date || !this.treasuryFilters.end_date) {
            ElMessage.warning('الرجاء تعبئة جميع الحقول.');
            return;
          }
          this.loading.treasury = true;
          try {
            const params = {
              treasury_id: this.treasuryFilters.treasury_id,
              start_date: this.formatDateForAPI(this.treasuryFilters.start_date),
              end_date: this.formatDateForAPI(this.treasuryFilters.end_date),
            };
            const response = await this.api.get('/reports/treasury-movement', { params });
            this.treasuryReportData = response.data;
          } catch (error) {
            ElMessage.error('فشل في توليد تقرير حركة الخزينة.');
          } finally {
            this.loading.treasury = false;
          }
        },
        async generateIncomeStatement() {
          if (!this.incomeFilters.start_date || !this.incomeFilters.end_date) {
            ElMessage.warning('الرجاء تعبئة جميع الحقول.');
            return;
          }
          this.loading.income = true;
          try {
            const params = {
              start_date: this.formatDateForAPI(this.incomeFilters.start_date),
              end_date: this.formatDateForAPI(this.incomeFilters.end_date),
            };
            const response = await this.api.get('/reports/income-statement', { params });
            this.incomeStatementData = response.data;
          } catch (error) {
            ElMessage.error('فشل في توليد تقرير قائمة الدخل.');
          } finally {
            this.loading.income = false;
          }
        },
        printReport(elementId) {
            const printableContent = document.getElementById(elementId).innerHTML;
            const printWindow = window.open('', '_blank');
            printWindow.document.write('<html><head><title>Financial Report</title>');
            printWindow.document.write('<style>body { direction: rtl; font-family: sans-serif; } table { width: 100%; border-collapse: collapse; } th, td { border: 1px solid #ddd; padding: 8px; } .total-summary, .net-profit { font-weight: bold; margin-top: 15px; } </style>');
            printWindow.document.write('</head><body>');
            printWindow.document.write(printableContent);
            printWindow.document.write('</body></html>');
            printWindow.document.close();
            printWindow.focus();
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 250);
        }
      },
    };
    </script>
    
    <style scoped>
    .financial-reports-container { padding: 20px; }
    .card-header { display: flex; justify-content: space-between; align-items: center; }
    .report-section { margin-top: 20px; }
    .report-display {
        margin-top: 30px;
        padding: 20px;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
    }
    .summary-descriptions { margin-top: 20px; }
    .total-summary {
        text-align: right;
        font-size: 1.1em;
        margin-top: 15px;
    }
    .net-profit {
        text-align: center;
        font-size: 1.3em;
        padding: 15px;
        border-radius: 8px;
    }
    .net-profit.profit {
        background-color: #f0f9eb;
        color: #67c23a;
    }
    .net-profit.loss {
        background-color: #fef0f0;
        color: #f56c6c;
    }
    .print-btn {
        margin-top: 20px;
        float: left;
    }
    </style>
    